import {
  Box,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import {
  CONTACT_STATUS_OPTIONS,
  CONTACT_STATUS_UNSET_FILTER,
  CONTACT_STATUS_UNSET_LABEL,
} from '../../constants/networking'

interface ContactFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  statusFilter: string
  onStatusChange: (value: string) => void
  priorityOnly: boolean
  onPriorityOnlyChange: (value: boolean) => void
  /**
   * Full-width controls in a single column, for the phone filter sheet. The
   * same controls either way — a phone must not get a smaller set of filters
   * than a desktop, only a different shape.
   */
  stacked?: boolean
  /**
   * Further status choices, listed after "Not yet actioned". The staff page
   * adds "Assigned, not reached out" (MWA-354); the portal passes none, so its
   * dropdown is unchanged.
   */
  extraStatusOptions?: { value: string; label: string }[]
}

const leadingStatusOptions = [
  { value: '', label: 'All Statuses' },
  { value: CONTACT_STATUS_UNSET_FILTER, label: CONTACT_STATUS_UNSET_LABEL },
]
const outreachStatusOptions = CONTACT_STATUS_OPTIONS.map(({ value, label }) => ({ value, label }))

export default function ContactFilters({
  search,
  onSearchChange,
  statusFilter,
  onStatusChange,
  priorityOnly,
  onPriorityOnlyChange,
  stacked = false,
  extraStatusOptions = [],
}: ContactFiltersProps) {
  const statusOptions = [...leadingStatusOptions, ...extraStatusOptions, ...outreachStatusOptions]
  return (
    <>
      {/* Full-size inside the sheet: MUI's `small` field is 40px, under the
          44px target a thumb needs. Inline on a desktop it stays small so the
          filter row keeps the density of the table beneath it. */}
      <TextField
        size={stacked ? 'medium' : 'small'}
        label="Search contacts"
        placeholder="Name, company or title"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={stacked ? { width: '100%' } : { minWidth: 200, maxWidth: 260, flex: '1 1 auto' }}
      />

      <FormControl size={stacked ? 'medium' : 'small'} sx={stacked ? { width: '100%' } : { minWidth: 150, maxWidth: 200, flex: '1 1 auto' }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          label="Status"
          onChange={(e) => onStatusChange(e.target.value)}
        >
          {statusOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={stacked ? { width: '100%' } : undefined}>
        <FormControlLabel
          control={
            <Switch
              checked={priorityOnly}
              onChange={(e) => onPriorityOnlyChange(e.target.checked)}
              size="small"
            />
          }
          label="Priority only"
          // The row, not just the switch, is the tap target on a phone.
          sx={{ ml: 0, mr: 0, minHeight: 44 }}
        />
      </Box>
    </>
  )
}
